import React from "react"
import autobind from "class-autobind"
import { Toggle, Card, CardHeader, CardText, SelectField, MenuItem } from "material-ui"
import strings from "../../localization/strings"
import AppStateStore from "../../stores/AppStateStore"
import * as accountActions from "../../actions/AccountActions"
import { IHierarchyItem, IHierarchyItemSettings, IHierarchyItemForAccountLevel, IJobDocument } from "../../models"
import * as hierarchyHelper from "../../helpers/hierarchyHelper"
import AccountStore from "../../stores/AccountStore"
import _ from "lodash"
import HierarchyJobVariations from "./hierarchy_job_variations/HierarchyJobVariations"
import { checkPositionFieldAvailable } from "../../helpers/positionHelper"

interface IProps {
    hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument
    inheritedHierarchyJobItem: IHierarchyItem | IJobDocument
    readonly: boolean
    handleUpdateJob(job: IJobDocument): Promise<void>
}
interface IState {
    expand: boolean
    isRoot: boolean
    canOverride: boolean
    positionSelection: number
}
const positionSelectionItems = ["User Union Hierarchy", "User Intersect Hierarchy"]

export default class HierarchySettingsPosition extends React.Component<IProps, IState>  {
    constructor(props) {
        super(props)
        this.state = {
            expand: false,
            isRoot: true,
            canOverride: false,
            positionSelection: 0
        }
        autobind(this)
    }

    componentDidMount() {
        this.checkIsRoot(this.props.hierarchyJobItem)
        this.getPositionSelection(this.props.inheritedHierarchyJobItem)
    }

    componentWillReceiveProps(nextProps: IProps) {
        !_.isEqual(this.props.hierarchyJobItem, nextProps.hierarchyJobItem) &&
            this.checkIsRoot(nextProps.hierarchyJobItem)
        !_.isEqual(this.props.inheritedHierarchyJobItem, nextProps.inheritedHierarchyJobItem) &&
            this.getPositionSelection(nextProps.inheritedHierarchyJobItem)
    }

    checkIsRoot(hierarchyJobItem: IHierarchyItemForAccountLevel | IHierarchyItem | IJobDocument) {
        hierarchyJobItem = hierarchyJobItem as IHierarchyItemForAccountLevel
        this.setState({ isRoot: hierarchyJobItem && hierarchyJobItem.level && hierarchyJobItem.level === -1 ? true : false }, () =>
            this.getCanOverride()
        )
    }

    getCanOverride() {
        if (!this.state.isRoot) {
            const hierarchyItem = this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)
            const inheritedHierarchyJobItemId = this.props.inheritedHierarchyJobItem ? this.props.inheritedHierarchyJobItem.Id : null
            this.setState({ canOverride: hierarchyItem.Id === inheritedHierarchyJobItemId })
        }
        else {
            this.setState({ canOverride: false })
        }
    }

    getPositionSelection(inheritedHierarchyJobItem: (IHierarchyItem | IJobDocument)) {
        const positionSelection =
            inheritedHierarchyJobItem &&
                inheritedHierarchyJobItem.HierarchyItemSettings &&
                inheritedHierarchyJobItem.HierarchyItemSettings.PositionSelectionSettings &&
                inheritedHierarchyJobItem.HierarchyItemSettings.PositionSelectionSettings.Type ?
                inheritedHierarchyJobItem.HierarchyItemSettings.PositionSelectionSettings.Type :
                0
        this.setState({ positionSelection })
    }

    handleSave(canOverride, positionSelection: number) {
        AppStateStore.setProgressSnackbarContent("loading")
        setTimeout(() => {
            this.setState({ canOverride, positionSelection }, () =>
                this.state.isRoot ?
                    this.updateAccountPositionSelectionSettings() :
                    this.updateHierarchyJobItemPositionSelectionSettings()
            )
        }, 1000)
    }

    updateAccountPositionSelectionSettings() {
        let hierarchyItemSettings = AccountStore.getCurrentAccount().HierarchyItemSettings
        if (hierarchyItemSettings) {
            hierarchyItemSettings.PositionSelectionSettings = { Type: this.state.positionSelection }
        } else {
            hierarchyItemSettings = { PositionSelectionSettings: { Type: this.state.positionSelection } } as IHierarchyItemSettings
        }
        accountActions.patchAccountHierarchySettings(AccountStore.getCurrentAccount().Id, { HierarchyItemSettings: hierarchyItemSettings })
    }

    updateHierarchyJobItemPositionSelectionSettings() {
        const hierarchyJobItem = this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)
        if (this.state.canOverride) {
            if (hierarchyJobItem.HierarchyItemSettings) {
                hierarchyJobItem.HierarchyItemSettings.PositionSelectionSettings = { Type: this.state.positionSelection }
            } else {
                hierarchyJobItem.HierarchyItemSettings = { PositionSelectionSettings: { Type: this.state.positionSelection } } as IHierarchyItemSettings
            }
        } else {
            if (hierarchyJobItem.HierarchyItemSettings) {
                hierarchyJobItem.HierarchyItemSettings.PositionSelectionSettings = null
            } else {
                hierarchyJobItem.HierarchyItemSettings = { PositionSelectionSettings: null } as IHierarchyItemSettings
            }
        }
        hierarchyJobItem.hasOwnProperty("AccountId") ?
            this.props.handleUpdateJob(hierarchyJobItem as IJobDocument).then(() => AppStateStore.setProgressSnackbarContent(null)) :
            accountActions.updateAccountHierarchyItem(AccountStore.getCurrentAccount().Id, hierarchyJobItem, true)
    }

    render() {
        return (
            <div className="HierarchySettingsPosition">
                <Card data-intercom-target={"menu-Hierarchy-Settings-Position"} expanded={this.state.expand} onExpandChange={expand => this.setState({ expand })}>
                    <CardHeader
                        title={checkPositionFieldAvailable('singular') ? AccountStore.getCurrentAccountSettings().CustomFieldSettings.PositionFieldLabels.SingularPositionLabel: strings.Position}
                        subtitle={[
                            <span key="title">
                                {this.state.isRoot ?
                                    `(${strings.not_inherited})` :
                                    !this.props.inheritedHierarchyJobItem ?
                                        `(${strings.formatString(strings.inherited_from_Account_x, AccountStore.getCurrentAccount().Name)})` :
                                        this.props.inheritedHierarchyJobItem.Id === (this.props.hierarchyJobItem as (IHierarchyItem | IJobDocument)).Id ?
                                            `(${strings.not_inherited})` :
                                            `(${strings.formatString(strings.inherited_from_x, `${hierarchyHelper.getHierarchyDefinitionByHierarchyItemId(this.props.inheritedHierarchyJobItem.Id).Label}:  ${(this.props.inheritedHierarchyJobItem as IHierarchyItem).Label}`)})`
                                }
                            </span>,
                            this.state.expand &&
                            <HierarchyJobVariations
                                key="hierarchy job variations"
                                settingPropertyName="PositionSelectionSettings"
                                hierarchyJobItem={this.props.hierarchyJobItem}
                                inheritedHierarchyJobItem={this.props.inheritedHierarchyJobItem}
                            />
                        ]}
                        actAsExpander={true}
                        showExpandableButton={true}
                        titleStyle={{ fontSize: 20, fontWeight: 500, display: "initial" }}
                        subtitleStyle={{ fontSize: 13, color: "rgb(97, 97, 97)", marginLeft: 10, display: "initial" }}
                    />
                    <CardText expandable={true}>
                        <div style={styles.body}>
                            {!this.state.isRoot &&
                                <Toggle
                                    data-intercom-target={"menu-Hierarchy-Settings-Position-Toggle-Override"}
                                    label={strings.Override}
                                    style={styles.toggle}
                                    toggled={this.state.canOverride}
                                    disabled={this.props.readonly}
                                    onToggle={(e, canOverride) =>
                                        AppStateStore.setConfirmDialogContent({
                                            content: strings.Are_you_sure_you_want_to_change_this_setting_of_hierarchy,
                                            confirm: () => canOverride ?
                                                this.setState({ canOverride }, () => this.updateHierarchyJobItemPositionSelectionSettings()) :
                                                this.handleSave(canOverride, this.state.positionSelection)
                                        })
                                    }
                                />
                            }
                            <SelectField
                                data-intercom-target={"menu-Hierarchy-Settings-Position-Select-Position"}
                                floatingLabelText={checkPositionFieldAvailable('singular') ? `${AccountStore.getCurrentAccountSettings().CustomFieldSettings.PositionFieldLabels.SingularPositionLabel} Selection`: "Position Selection"}
                                value={this.state.positionSelection}
                                onChange={(e, key, payload) =>
                                    AppStateStore.setConfirmDialogContent({
                                        content: strings.Are_you_sure_you_want_to_change_this_setting_of_hierarchy,
                                        confirm: () =>
                                            this.setState({ positionSelection: key }, () =>
                                                this.handleSave(this.state.canOverride, this.state.positionSelection)
                                            )
                                    })
                                }
                                disabled={(this.state.isRoot || this.state.canOverride) && !this.props.readonly ? false : true}
                            >
                                {positionSelectionItems.map((item, key) =>
                                    <MenuItem value={key} primaryText={item} key={key} />
                                )}
                            </SelectField>
                        </div>
                    </CardText>
                </Card>
            </div>
        )
    }
}

const styles = {
    toggle: {
        marginBottom: 20
    },
    body: {
        maxWidth: 300
    },
    titleExpandOn: {
        marginBottom: 30
    },
    titleExpandOff: {
        marginBottom: 0
    }
}
